import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { searchAutocomplete } from '../api/client';

const typeChip = (t) => {
  const map = {
    tool: 'T',
    creator: 'C',
    company: 'Co',
    prompt: 'P',
    course: 'Cs',
    agent: 'A',
    gpt: 'GPT',
    mcp: 'MCP',
  };
  return map[t] || (t || '').slice(0, 1).toUpperCase();
};

const routeFor = (r) => {
  if (r.resource_type === 'creator') return `/creator/${r.slug}`;
  if (r.resource_type === 'company') return `/company/${r.slug}`;
  return `/tool/${r.slug}`;
};

export default function SearchAutocomplete({ placeholder = 'Search tools, creators, companies…', variant = 'default', onQueryChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    onQueryChange?.(q);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const h = setTimeout(() => {
      searchAutocomplete(q.trim(), 10).then(setResults).catch(() => setResults([]));
    }, 120);
    return () => clearTimeout(h);
  }, [q, onQueryChange]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      const chosen = results[active >= 0 ? active : 0];
      if (chosen) { setOpen(false); nav(routeFor(chosen)); }
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className={`search-ac ${variant}`} ref={boxRef} data-testid="search-autocomplete">
      <Search className="search-icon" size={14} />
      <input
        className="search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => q && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        type="text"
        data-testid="search-autocomplete-input"
      />
      {q && (
        <button
          type="button"
          className="search-clear"
          onClick={() => { setQ(''); setResults([]); }}
          aria-label="Clear"
        >
          <X size={12} />
        </button>
      )}
      {open && results.length > 0 && (
        <div className="search-ac-menu" role="listbox">
          {results.map((r, i) => (
            <Link
              key={r.slug}
              to={routeFor(r)}
              className={`search-ac-row ${i === active ? 'active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => setOpen(false)}
              data-testid={`search-ac-row-${r.slug}`}
            >
              <span className="search-ac-name">{r.name}</span>
              <span className="search-ac-cat">{r.primary_category}</span>
              <span className="search-ac-type">{typeChip(r.resource_type)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
